import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, MapPin } from "lucide-react";

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  type?: "location" | "general";
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = "Application Error",
  message,
  type = "general",
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Blurred Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Icon Header */}
            <div className="pt-8 pb-4 flex justify-center">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  type === "location" ? "bg-orange-100" : "bg-red-100"
                }`}
              >
                {type === "location" ? (
                  <MapPin className="w-8 h-8 text-orange-500" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                )}
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
              <p className="text-gray-600 leading-relaxed">{message}</p>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              <button
                onClick={onClose}
                className="w-full py-3 px-4 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-[#8B52FF]/25 transition-all"
              >
                I Understand
              </button>
            </div>

            {/* Supported Countries Info (for location errors) */}
            {/* {type === "location" && (
              <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  We currently accept applications from:
                </p>
                <div className="flex justify-center gap-3 mt-2">
                  <span className="text-xs bg-gray-100 px-3 py-1 rounded-full">
                    🇺🇸 United States
                  </span>
                  <span className="text-xs bg-gray-100 px-3 py-1 rounded-full">
                    🇬🇧 United Kingdom
                  </span>
                  <span className="text-xs bg-gray-100 px-3 py-1 rounded-full">
                    🇨🇦 Canada
                  </span>
                </div>
              </div>
            )} */}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ErrorModal;
